import { NegotiationType } from '../components/customer-companies-management/lib'
import { Customer, Document, DocumentFinding, SystemUser } from '../types'
import { CustomerCompany, CustomerCompanyFunding, Payment, UnidentifiedCustomer } from '../types'

export const normalizeCustomerData = (customerDataToNormalize: Customer | unknown) => {
  const customerData = customerDataToNormalize as Customer
  const createdAt = customerData.createdAt ? new Date(customerData.createdAt) : new Date()
  const modifiedAt = customerData.modifiedAt ? new Date(customerData.modifiedAt) : new Date()

  return {
    _id: String(customerData._id),
    createdAt: createdAt.toISOString(),
    modifiedAt: modifiedAt.toISOString(),
    documentType: customerData.documentType,
    belongsTo: customerData.belongsTo ?? '',
    documentNumber: customerData.documentNumber,
    bankAccountsData: customerData.bankAccountsData,
    registeredFullName: customerData.registeredFullName,
    customerCompanyPocketsRelated: customerData.customerCompanyPocketsRelated ?? []
  }
}

export const normalizePaymentData = (paymentDataToNormalize: Payment | unknown) => {
  const paymentData = paymentDataToNormalize as Payment
  const paidAt = paymentData.paidAt ? new Date(paymentData.paidAt) : new Date()
  const createdAt = paymentData.createdAt ? new Date(paymentData.createdAt) : new Date()

  return {
    status: paymentData.status,
    amount: paymentData.amount,
    _id: String(paymentData._id),
    tags: paymentData.tags ?? [],
    createdAt: createdAt.toISOString(),
    customerId: paymentData.customerId,
    documentId: paymentData.documentId,
    bankName: paymentData.bankName ?? '',
    belongsTo: paymentData.belongsTo || '',
    novelties: paymentData.novelties ?? [],
    currency: paymentData.currency || 'cop',
    bankAchCode: paymentData.bankAchCode ?? '',
    description: paymentData.description || '',
    referenceNumber: paymentData.referenceNumber,
    bankAccountNumber: paymentData.bankAccountNumber,
    fundsFromPocket: paymentData.fundsFromPocket ?? '',
    bankAccountType: paymentData.bankAccountType ?? '',
    paidAt: paymentData.paidAt ? paidAt.toISOString() : '',
    bankAccountStatus: paymentData.bankAccountStatus ?? '',
    additionalValues: paymentData.additionalValues || undefined,
    backgroundReviewed: paymentData.backgroundReviewed ?? false,
    cobrePaymentReference: paymentData.cobrePaymentReference ?? '',
    unidentifiedCustomerId: paymentData.unidentifiedCustomerId ?? '',
    customerRegisteredFullName: paymentData.customerRegisteredFullName ?? ''
  }
}

export const normalizeDocumentData = (documentDataToNormalize: Document | unknown) => {
  const documentData = documentDataToNormalize as Document
  const createdAt = documentData.createdAt ? new Date(documentData.createdAt) : new Date()
  const approvedAt = documentData.approvedAt ? new Date(documentData.approvedAt) : new Date()

  return {
    status: documentData.status,
    _id: String(documentData._id),
    belongsTo: documentData.belongsTo,
    createdAt: createdAt.toISOString(),
    uploadedBy: documentData.uploadedBy,
    approvedAt: approvedAt.toISOString(),
    originalName: documentData.originalName,
    approvedBy: documentData.approvedBy ?? '',
    fileSystemName: documentData.fileSystemName,
    totalOfRegistries: documentData.totalOfRegistries,
    errorDescription: documentData.errorDescription ?? '',
    uploadedByFullName: documentData.uploadedByFullName ?? '',
    cobrePaymentReference: documentData.cobrePaymentReference ?? [],
    totalOfNoveltiesRejected: documentData.totalOfNoveltiesRejected ?? 0,
    totalOfNoveltiesReviewed: documentData.totalOfNoveltiesReviewed ?? 0,
    customerCompanyPocketUsed: documentData.customerCompanyPocketUsed ?? '',
    totalOfNoveltiesEmptyBankName: documentData.totalOfNoveltiesEmptyBankName ?? 0,
    totalOfNoveltiesUnknownBankName: documentData.totalOfNoveltiesUnknownBankName ?? 0,
    totalOfNoveltiesWrongDocumentType: documentData.totalOfNoveltiesWrongDocumentType ?? 0,
    totalOfNoveltiesWrongPaymentAmount: documentData.totalOfNoveltiesWrongPaymentAmount ?? 0,
    totalOfNoveltiesEmptyPaymentAmount: documentData.totalOfNoveltiesEmptyPaymentAmount ?? 0,
    totalOfNoveltiesBackgroundResearch: documentData.totalOfNoveltiesBackgroundResearch ?? 0,
    totalOfNoveltiesEmptyRegisteredName: documentData.totalOfNoveltiesEmptyRegisteredName ?? 0,
    totalOfNoveltiesEmptyDocumentNumber: documentData.totalOfNoveltiesEmptyDocumentNumber ?? 0,
    totalOfNoveltiesWrongDocumentNumber: documentData.totalOfNoveltiesWrongDocumentNumber ?? 0,
    totalOfNoveltiesEmptyBankAccountType: documentData.totalOfNoveltiesEmptyBankAccountType ?? 0,
    totalOfNoveltiesWrongBankAccountType: documentData.totalOfNoveltiesWrongBankAccountType ?? 0,
    totalOfNoveltiesEmptyPaymentReference: documentData.totalOfNoveltiesEmptyPaymentReference ?? 0,
    totalOfNoveltiesWrongBankAccountNumber: documentData.totalOfNoveltiesWrongBankAccountNumber ?? 0,
    totalOfNoveltiesEmptyBankAccountNumber: documentData.totalOfNoveltiesEmptyBankAccountNumber ?? 0,
    totalOfNoveltiesDuplicatePaymentReference: documentData.totalOfNoveltiesDuplicatePaymentReference ?? 0,
    totalOfNoveltiesDuplicateBankAccountNumber: documentData.totalOfNoveltiesDuplicateBankAccountNumber ?? 0,
    totalOfNoveltiesBankAccountNumberNotRegistered: documentData.totalOfNoveltiesBankAccountNumberNotRegistered ?? 0
  }
}

export const normalizeDocumentFindingData = (documentFindingDataToNormalize: DocumentFinding | unknown) => {
  const documentFindingData = documentFindingDataToNormalize as DocumentFinding
  const createdAt = documentFindingData.createdAt ? new Date(documentFindingData.createdAt) : new Date()

  return {
    createdAt: createdAt.toISOString(),
    _id: String(documentFindingData._id),
    customerId: documentFindingData.customerId,
    documentId: documentFindingData.documentId,
    findingType: documentFindingData.findingType,
    paymentData: documentFindingData.paymentData ?? null,
    customerData: documentFindingData.customerData ?? {},
    bankAccountData: documentFindingData.bankAccountData,
    paymentReferenceNumber: documentFindingData.paymentReferenceNumber ?? ''
  }
}

export const normalizeSystemUserData = (systemUserDataToNormalize: SystemUser | unknown) => {
  const systemUserData = systemUserDataToNormalize as SystemUser
  const createdAt = systemUserData.createdAt ? new Date(systemUserData.createdAt) : new Date()

  return {
    role: systemUserData.role,
    _id: String(systemUserData._id),
    username: systemUserData.username,
    lastName: systemUserData.lastName,
    createdAt: createdAt.toISOString(),
    firstName: systemUserData.firstName,
    activated: systemUserData.activated,
    description: systemUserData.description || '',
    adminOfPockets: systemUserData.adminOfPockets || []
  }
}

export const normalizeCustomerCompanyData = (customerCompanyDataToNormalize: CustomerCompany | unknown) => {
  const customerCompanyData = customerCompanyDataToNormalize as CustomerCompany
  const createdAt = customerCompanyData.createdAt ? new Date(customerCompanyData.createdAt) : new Date()

  return {
    createdAt: createdAt.toISOString(),
    _id: String(customerCompanyData._id),
    balance: customerCompanyData.balance,
    activated: customerCompanyData.activated,
    businessCompany: customerCompanyData.businessCompany,
    businessOwner: customerCompanyData.businessOwner || '',
    preferredLanguage: customerCompanyData.preferredLanguage,
    companyMainUserData: customerCompanyData.companyMainUserData || null,
    businessOwnerDocumentation: customerCompanyData.businessOwnerDocumentation,
    businessCompanyDocumentation: customerCompanyData.businessCompanyDocumentation
  }
}

export const normalizeCustomerCompanyFundingData = (customerCompanyFundingDataToNormalize: CustomerCompanyFunding | unknown) => {
  const customerCompanyFundingData = customerCompanyFundingDataToNormalize as CustomerCompanyFunding
  const createdAt = customerCompanyFundingData.createdAt ? new Date(customerCompanyFundingData.createdAt) : new Date()

  return {
    createdAt: createdAt.toISOString(),
    status: customerCompanyFundingData.status,
    _id: String(customerCompanyFundingData._id),
    createdBy: customerCompanyFundingData.createdBy,
    assignedBy: customerCompanyFundingData.assignedBy,
    rejectedBy: customerCompanyFundingData.rejectedBy,
    assignedTo: customerCompanyFundingData.assignedTo,
    description: customerCompanyFundingData.description || '',
    expectedProfit: customerCompanyFundingData.expectedProfit || 0,
    bankingPartner: customerCompanyFundingData.bankingPartner || '',
    fundingSaleRate: customerCompanyFundingData.fundingSaleRate || 0,
    saleTradeResult: customerCompanyFundingData.saleTradeResult || 0,
    purchaseTradeResult: customerCompanyFundingData.purchaseTradeResult,
    fundingPurchaseRate: customerCompanyFundingData.fundingPurchaseRate,
    fundingSaleQuantity: customerCompanyFundingData.fundingSaleQuantity || 0,
    fundingPurchaseQuantity: customerCompanyFundingData.fundingPurchaseQuantity,
    purchaseNetValueResult: customerCompanyFundingData.purchaseNetValueResult || 0,
    expectedProfitCurrency: customerCompanyFundingData.expectedProfitCurrency || 'COP',
    negotiationType: customerCompanyFundingData.negotiationType ?? NegotiationType.NEXT_DAY,
    negotiationAdditionalPercentage: customerCompanyFundingData.negotiationAdditionalPercentage || 0
  }
}

export const normalizeUnidentifiedCustomerData = (unidentifiedCustomerDataToNormalize: UnidentifiedCustomer | unknown) => {
  const unidentifiedCustomerData = unidentifiedCustomerDataToNormalize as UnidentifiedCustomer
  const createdAt = unidentifiedCustomerData.createdAt ? new Date(unidentifiedCustomerData.createdAt) : new Date()
  const modifiedAt = unidentifiedCustomerData.modifiedAt ? new Date(unidentifiedCustomerData.modifiedAt) : new Date()

  return {
    createdAt: createdAt.toISOString(),
    modifiedAt: modifiedAt.toISOString(),
    status: unidentifiedCustomerData.status,
    _id: String(unidentifiedCustomerData._id),
    documentType: unidentifiedCustomerData.documentType,
    belongsTo: unidentifiedCustomerData.belongsTo ?? '',
    documentNumber: unidentifiedCustomerData.documentNumber ?? '',
    bankAccountsData: unidentifiedCustomerData.bankAccountsData,
    registeredFullName: unidentifiedCustomerData.registeredFullName
  }
}
